import React, { useRef, useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Cookies from "js-cookie";
import Sidenav from "../components/Sidenav";
import "../Console.css";

const styles = (
  <style>{`
    /* ── Top bar ── */
    .ae-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      height: 56px;
      background: #fff;
      flex-shrink: 0;
    }
    .ae-back {
      display: flex;
      align-items: center;
      gap: 7px;
      background: none;
      border: none;
      color: #64748b;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      padding: 6px 10px 6px 2px;
      border-radius: 8px;
      transition: color 0.12s;
    }
    .ae-back:hover { color: #111827; }

    .ae-btn-draft {
      background: none;
      border: 1.5px solid #e2e8f0;
      color: #374151;
      font-size: 13px;
      font-weight: 600;
      padding: 7px 18px;
      border-radius: 20px;
      cursor: pointer;
      transition: border-color 0.12s, background 0.12s;
    }
    .ae-btn-draft:hover { background: #f8fafc; border-color: #cbd5e1; }
    .ae-btn-draft:disabled { opacity: 0.45; cursor: not-allowed; }

    .ae-btn-publish {
      background: #111827;
      border: none;
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      padding: 8px 22px;
      border-radius: 20px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 7px;
      transition: background 0.12s;
    }
    .ae-btn-publish:hover { background: #1f2937; }
    .ae-btn-publish:disabled { opacity: 0.45; cursor: not-allowed; }

    /* ── Panes ── */
    .ae-panes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      flex: 1;
      overflow: hidden;
    }
    @media (max-width: 900px) { .ae-panes { grid-template-columns: 1fr; } }

    /* ── Write pane ── */
    .ae-write-pane {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border-right: 1px solid #1a1a1a;
      background: #0a0a0a;
    }

    .ae-write-head {
      padding: 28px 36px 0;
      flex-shrink: 0;
    }

    .ae-cover-preview {
      width: 100%;
      height: 160px;
      object-fit: cover;
      border-radius: 12px;
      margin-bottom: 20px;
      display: block;
    }

    .ae-title-input {
      width: 100%;
      border: none;
      outline: none;
      font-size: 1.9rem;
      font-weight: 900;
      color: #fff;
      line-height: 1.2;
      resize: none;
      overflow: hidden;
      background: transparent;
      font-family: inherit;
      padding: 0;
      display: block;
      margin-bottom: 16px;
    }
    .ae-title-input::placeholder { color: #444; }

    .ae-meta-row {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
    .ae-meta-pill {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #141414;
      border: 1px solid #222;
      border-radius: 9999px;
      padding: 5px 13px 5px 10px;
      transition: border-color 0.12s;
    }
    .ae-meta-pill:focus-within { border-color: #555; }
    .ae-meta-pill i { color: #aaa; font-size: 11px; }
    .ae-meta-pill input {
      border: none;
      background: transparent;
      outline: none;
      font-size: 13px;
      color: #fff;
      width: 140px;
    }
    .ae-meta-pill input::placeholder { color: #555; }

    .ae-write-divider { height: 1px; background: #1a1a1a; margin-bottom: 0; }

    /* ── Cover image ── */
    .ae-cover-area {
      width: 100%;
      margin-bottom: 20px;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
    }
    .ae-cover-empty {
      width: 100%;
      height: 130px;
      border: 2px dashed #222;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #666;
      font-size: 13px;
      font-weight: 600;
      transition: border-color 0.15s, background 0.15s;
    }
    .ae-cover-empty:hover { border-color: #555; background: #141414; color: #999; }
    .ae-cover-empty i { font-size: 1.4rem; }
    .ae-cover-filled {
      position: relative;
      width: 100%;
      height: 160px;
    }
    .ae-cover-filled img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      border-radius: 12px;
    }
    .ae-cover-overlay {
      position: absolute;
      inset: 0;
      border-radius: 12px;
      background: rgba(0,0,0,0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .ae-cover-filled:hover .ae-cover-overlay { opacity: 1; }
    .ae-cover-action {
      background: rgba(255,255,255,0.15);
      border: 1.5px solid rgba(255,255,255,0.5);
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      padding: 6px 14px;
      border-radius: 9999px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: background 0.12s;
      backdrop-filter: blur(4px);
    }
    .ae-cover-action:hover { background: rgba(255,255,255,0.28); }
    .ae-cover-action.remove:hover { background: rgba(220,38,38,0.55); border-color: transparent; }

    /* ── Toolbar ── */
    .ae-toolbar {
      display: flex;
      align-items: center;
      gap: 1px;
      padding: 6px 36px;
      border-bottom: 1px solid #1a1a1a;
      border-top: 1px solid #1a1a1a;
      background: #111;
      flex-shrink: 0;
      flex-wrap: wrap;
    }
    .ae-tb-btn {
      border: none;
      background: transparent;
      padding: 5px 9px;
      border-radius: 6px;
      font-size: 13px;
      color: #fff;
      cursor: pointer;
      font-weight: 700;
      line-height: 1;
      min-width: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.1s, color 0.1s;
    }
    .ae-tb-btn:hover { background: #2a2a2a; color: #fff; }
    .ae-tb-sep { width: 1px; height: 15px; background: #222; margin: 0 4px; flex-shrink: 0; }

    /* ── Textarea ── */
    .ae-textarea {
      flex: 1;
      width: 100%;
      padding: 20px 36px 40px;
      border: none;
      resize: none;
      font-family: 'Courier New', Courier, monospace;
      font-size: 14px;
      line-height: 1.9;
      color: #fff;
      background: #0a0a0a;
      outline: none;
    }
    .ae-textarea::placeholder { color: #444; font-style: italic; font-family: inherit; }

    /* ── Preview pane ── */
    .ae-preview-pane {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: #f8f9fa;
    }
    .ae-preview-bar {
      padding: 9px 20px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #94a3b8;
      background: #f8f9fa;
      flex-shrink: 0;
    }
    .ae-preview-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 40px 36px 60px;
      background: #f8f9fa;
    }

    .pr-cover { width:100%; max-height:380px; object-fit:cover; border-radius:18px; margin-bottom:28px; display:block; }
    .pr-title { font-size:clamp(1.5rem, 3vw, 2.2rem); font-weight:900; color:#111827; line-height:1.18; margin:0 0 20px; }
    .pr-meta  { display:flex; align-items:center; gap:14px; flex-wrap:wrap; font-size:13px; color:#6b7280; padding-bottom:16px; border-bottom:1px solid #e5e7eb; margin-bottom:24px; }
    .pr-author { display:flex; align-items:center; gap:8px; font-weight:700; color:#374151; }
    .pr-author-avatar {
      width:32px; height:32px; border-radius:50%;
      background:linear-gradient(135deg, #f97316, #ef4444);
      display:flex; align-items:center; justify-content:center;
      color:#fff; font-weight:800; font-size:13px; flex-shrink:0;
    }

    .ae-preview-scroll h1 { font-size:clamp(1.4rem, 3vw, 1.9rem); font-weight:800; margin:2rem 0 0.75rem; color:#111827; line-height:1.25; }
    .ae-preview-scroll h2 { font-size:clamp(1.2rem, 2.5vw, 1.5rem); font-weight:700; margin:1.75rem 0 0.6rem; color:#111827; }
    .ae-preview-scroll h3 { font-size:clamp(1rem, 2vw, 1.2rem); font-weight:700; margin:1.5rem 0 0.5rem; color:#111827; }
    .ae-preview-scroll p  { font-size:clamp(15px, 2vw, 17px); line-height:1.85; color:#374151; margin-bottom:1.1rem; }
    .ae-preview-scroll img { max-width:100%; border-radius:12px; margin:1rem 0; display:block; box-shadow:0 2px 16px rgba(0,0,0,0.08); }
    .ae-preview-scroll figure { margin:1rem 0; }
    .ae-preview-scroll figure img { margin:0; width:100%; }
    .ae-preview-scroll figcaption { text-align:center; font-size:13px; color:#9ca3af; font-style:italic; margin-top:10px; }
    .ae-preview-scroll blockquote { border-left:4px solid #f97316; padding:12px 20px; background:#fff7f3; border-radius:0 10px 10px 0; margin:1.5rem 0; font-style:italic; color:#374151; font-size:16px; }
    .ae-preview-scroll code { background:#f3f4f6; padding:2px 7px; border-radius:4px; font-size:14px; font-family:'Courier New',monospace; }
    .ae-preview-scroll pre { background:#1e293b; color:#e2e8f0; padding:20px; border-radius:12px; overflow-x:auto; margin:1rem 0; }
    .ae-preview-scroll pre code { background:transparent; color:inherit; padding:0; }
    .ae-preview-scroll ul, .ae-preview-scroll ol { padding-left:1.5rem; margin-bottom:1rem; }
    .ae-preview-scroll li { font-size:clamp(15px, 2vw, 17px); line-height:1.8; color:#374151; margin-bottom:6px; }
    .ae-preview-scroll hr { border:none; border-top:1px solid #e5e7eb; margin:2rem 0; }
    .ae-preview-scroll a { color:#f97316; text-decoration:underline; text-underline-offset:3px; }
    .ae-preview-scroll strong { color:#111827; }

    .ae-preview-empty {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #cbd5e1;
      gap: 10px;
      text-align: center;
    }
    .ae-preview-empty i { font-size: 1.8rem; }
    .ae-preview-empty p { font-size: 13px; margin: 0; }
  `}</style>
);

function insertAtCursor(taRef, content, setContent, before, after = "") {
  const ta = taRef.current;
  if (!ta) return;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = content.substring(start, end);
  const next = content.substring(0, start) + before + selected + after + content.substring(end);
  setContent(next);
  setTimeout(() => {
    ta.focus();
    ta.selectionStart = start + before.length;
    ta.selectionEnd = start + before.length + selected.length;
  }, 0);
}

function prependLine(taRef, content, setContent, prefix) {
  const ta = taRef.current;
  if (!ta) return;
  const start = ta.selectionStart;
  const lineStart = content.lastIndexOf("\n", start - 1) + 1;
  const next = content.substring(0, lineStart) + prefix + content.substring(lineStart);
  setContent(next);
  setTimeout(() => {
    ta.focus();
    ta.selectionStart = start + prefix.length;
    ta.selectionEnd = start + prefix.length;
  }, 0);
}

function getInitials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

const ArticleEditor = ({ domain, userData }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  // Cover image: S3 key (what gets saved) + preview URL (what the editor shows)
  const [coverKey, setCoverKey] = useState("");
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);

  // Map of S3 key → signed preview URL for inline images inserted in content
  const imageKeyMap = useRef(new Map());

  const editorRef = useRef(null);
  const titleRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);

  // Load article for editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(`/api/articles/${id}`, { credentials: "same-origin" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setTitle(data.title || "");
        setAuthor(data.author || "");
        setContent(data.content || "");
        setCoverKey(data.headerImage || "");
        setCoverPreviewUrl(data.headerImageUrl || "");
        // Populate imageKeyMap so the preview can resolve inline images
        (data.contentImageUrls || []).forEach(({ key, url }) => {
          imageKeyMap.current.set(key, url);
        });
      } catch (e) {
        console.error("Failed to load article:", e);
        alert("Failed to load article.");
      }
    })();
  }, [id, isEdit]);

  // Upload cover image immediately on selection
  const handleCoverSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    // Show local preview immediately while uploading
    const localUrl = URL.createObjectURL(file);
    setCoverPreviewUrl(localUrl);
    setCoverUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/articles/images?type=cover", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { key, previewUrl } = await res.json();
      setCoverKey(key);
      URL.revokeObjectURL(localUrl);
      setCoverPreviewUrl(previewUrl);
    } catch (err) {
      console.error("Cover upload failed:", err);
      URL.revokeObjectURL(localUrl);
      setCoverPreviewUrl("");
      alert("Cover image upload failed. Please try again.");
    } finally {
      setCoverUploading(false);
    }
  };

  const handleCoverRemove = (e) => {
    e.stopPropagation();
    setCoverKey("");
    setCoverPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Replace S3 keys in content with signed preview URLs for the editor preview pane
  const resolveContentForPreview = useCallback((raw) => {
    let resolved = raw;
    for (const [key, url] of imageKeyMap.current.entries()) {
      resolved = resolved.split(key).join(url);
    }
    return resolved;
  }, []);

  const autoResizeTitle = useCallback(() => {
    const ta = titleRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  }, []);

  useEffect(() => { autoResizeTitle(); }, [title, autoResizeTitle]);

  const ins = (before, after = "") =>
    insertAtCursor(editorRef, content, setContent, before, after);
  const pre = (prefix) =>
    prependLine(editorRef, content, setContent, prefix);

  const insertImage = () => imgInputRef.current?.click();

  const handleContentImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/articles/images?type=content", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { key, previewUrl } = await res.json();
      imageKeyMap.current.set(key, previewUrl);
      const caption = window.prompt("Image caption (optional):") || "";
      ins(`\n![${caption}](${key})\n`);
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Image upload failed. Please try again.");
    }
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:");
    if (url) ins("[", `](${url})`);
  };

  const handleSave = async (status) => {
    if (!title.trim()) {
      alert("Please add a title before saving.");
      return;
    }
    if (coverUploading) {
      alert("Cover image is still uploading. Please wait.");
      return;
    }
    if (status === "published") {
      const missing = [];
      if (!coverKey) missing.push("cover image");
      if (!author.trim()) missing.push("author name");
      if (missing.length) {
        alert(`Please add a ${missing.join(" and ")} before publishing.`);
        return;
      }
    }
    setSaving(true);
    try {
      const userId = Cookies.get("userId");
      const usedContentImages = [...imageKeyMap.current.keys()].filter((k) =>
        content.includes(k)
      );
      const payload = {
        title,
        author,
        content,
        headerImage: coverKey,
        contentImages: usedContentImages,
        status,
        userId,
      };

      const res = await fetch(
        isEdit ? `/api/articles/${id}` : "/api/articles",
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      navigate("/articles");
    } catch (e) {
      console.error(e);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const hasContent = !!(title || content || coverPreviewUrl);

  return (
    <>
      <Helmet>
        <title>{isEdit ? "Edit Article" : "New Article"} | BLEEP</title>
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            height: "calc(100vh - 94px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Top bar */}
          <div className="ae-topbar">
            <button className="ae-back" onClick={() => navigate("/articles")}>
              <i className="fas fa-arrow-left" />
              Articles
            </button>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="ae-btn-draft"
                onClick={() => handleSave("draft")}
                disabled={saving}
              >
                Save draft
              </button>
              <button
                className="ae-btn-publish"
                onClick={() => handleSave("published")}
                disabled={saving}
              >
                <i className="fas fa-paper-plane" style={{ fontSize: 11 }} />
                {saving ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>

          {/* Panes */}
          <div className="ae-panes" style={{ flex: 1, overflow: "hidden" }}>
            {/* Write pane */}
            <div className="ae-write-pane">
              <div className="ae-write-head">
                <textarea
                  ref={titleRef}
                  className="ae-title-input"
                  placeholder="Untitled article"
                  value={title}
                  rows={1}
                  onChange={(e) => setTitle(e.target.value)}
                />

                {/* Cover image picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleCoverSelect}
                />
                {/* Inline content image picker */}
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleContentImageSelect}
                />
                <div
                  className="ae-cover-area"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {coverPreviewUrl ? (
                    <div className="ae-cover-filled">
                      <img src={coverPreviewUrl} alt="cover" style={{ opacity: coverUploading ? 0.4 : 1 }} />
                      {coverUploading ? (
                        <div className="ae-cover-overlay" style={{ opacity: 1 }}>
                          <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
                            <i className="fas fa-spinner fa-spin me-2" />Uploading…
                          </span>
                        </div>
                      ) : (
                        <div className="ae-cover-overlay">
                          <button
                            className="ae-cover-action"
                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                          >
                            <i className="fas fa-camera" /> Change
                          </button>
                          <button
                            className="ae-cover-action remove"
                            onClick={handleCoverRemove}
                          >
                            <i className="fas fa-trash" /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="ae-cover-empty">
                      <i className="fas fa-image" />
                      <span>Add cover image</span>
                    </div>
                  )}
                </div>

                <div className="ae-meta-row">
                  <div className="ae-meta-pill">
                    <i className="fas fa-user" />
                    <input
                      placeholder="Author name"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                    />
                  </div>
                </div>

                <div className="ae-write-divider" />
              </div>

              {/* Toolbar */}
              <div className="ae-toolbar">
                <button className="ae-tb-btn" title="Bold" onClick={() => ins("**", "**")}><b>B</b></button>
                <button className="ae-tb-btn" title="Italic" onClick={() => ins("*", "*")} style={{ fontStyle: "italic" }}>I</button>
                <div className="ae-tb-sep" />
                <button className="ae-tb-btn" title="Heading 1" onClick={() => pre("# ")}>H1</button>
                <button className="ae-tb-btn" title="Heading 2" onClick={() => pre("## ")}>H2</button>
                <button className="ae-tb-btn" title="Heading 3" onClick={() => pre("### ")}>H3</button>
                <div className="ae-tb-sep" />
                <button className="ae-tb-btn" title="Blockquote" onClick={() => pre("> ")}><i className="fas fa-quote-right" /></button>
                <button className="ae-tb-btn" title="Bullet list" onClick={() => pre("- ")}><i className="fas fa-list-ul" /></button>
                <button className="ae-tb-btn" title="Code" onClick={() => ins("`", "`")}><i className="fas fa-code" /></button>
                <div className="ae-tb-sep" />
                <button className="ae-tb-btn" title="Link" onClick={insertLink}><i className="fas fa-link" /></button>
                <button className="ae-tb-btn" title="Insert image" onClick={insertImage}><i className="fas fa-image" /></button>
                <div className="ae-tb-sep" />
                <button className="ae-tb-btn" title="Divider" onClick={() => ins("\n\n---\n\n")}>—</button>
              </div>

              {/* Writing area */}
              <textarea
                ref={editorRef}
                className="ae-textarea"
                placeholder="Start writing…"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                spellCheck
              />
            </div>

            {/* Preview pane */}
            <div className="ae-preview-pane">
              <div className="ae-preview-bar">Preview</div>
              <div className="ae-preview-scroll">
                {!hasContent ? (
                  <div className="ae-preview-empty">
                    <i className="fas fa-eye" />
                    <p>Your article preview<br />will appear here</p>
                  </div>
                ) : (
                  <>
                    <div className="pr-title">
                      {title || <span style={{ color: "#e2e8f0" }}>Untitled article</span>}
                    </div>
                    {coverPreviewUrl && (
                      <img
                        src={coverPreviewUrl}
                        alt="cover"
                        className="pr-cover"
                      />
                    )}
                    <div className="pr-meta">
                      {author && (
                        <div className="pr-author">
                          <div className="pr-author-avatar">{getInitials(author)}</div>
                          {author}
                        </div>
                      )}
                      <span>
                        <i className="fas fa-calendar me-1" />
                        {new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <ReactMarkdown
                      components={{
                        img: ({ src, alt }) => (
                          <figure>
                            <img src={src} alt={alt} />
                            {alt && <figcaption>{alt}</figcaption>}
                          </figure>
                        ),
                      }}
                    >
                      {resolveContentForPreview(content)}
                    </ReactMarkdown>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticleEditor;
